import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Loader } from 'components';
import * as PDFJSViewer from 'pdfjs-dist/web/pdf_viewer.js';
import * as pdfjsLib from 'pdfjs-dist/webpack.js';
import 'pdfjs-dist/web/pdf_viewer.css';
import { useMountedState } from 'utils/hooks';
import { Box } from '@mui/material';

const ERROR_MARGIN = 50; // pixels

const PDFViewer = ({ url, setReachBottom, sx, id = 'viewer', ...rest }) => {
    const viewerRef = useRef();
    const mounted = useMountedState();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!url) return; // prevent uncaught plugin error
        const eventBus = new PDFJSViewer.EventBus();

        const viewerContainer = document.getElementById(id);
        const pdfViewer = new PDFJSViewer.PDFViewer({
            container: viewerContainer,
            eventBus: eventBus,
        });

        eventBus.on('pagesinit', function () {
            if (mounted) pdfViewer.currentScaleValue = 'page-width';
        });
        const setScale = () => (pdfViewer.currentScaleValue = 'page-width');

        setLoading(true);
        const loadingTask = pdfjsLib.getDocument(url);
        loadingTask.promise
            .then(doc => {
                if (mounted) {
                    setLoading(false);
                    pdfViewer.setDocument(doc);
                    window.addEventListener('resize', setScale);
                }
            })
            .catch(reason => {
                setLoading(false);
                console.log(reason);
            });

        return () => {
            window.removeEventListener('resize', setScale);
        };
    }, [url, mounted, id]);

    const onScroll = useCallback(() => {
        if (viewerRef.current) {
            const { scrollTop, scrollHeight, offsetHeight } = viewerRef.current;
            if (Math.ceil(scrollTop + offsetHeight) >= scrollHeight - ERROR_MARGIN) {
                setReachBottom?.(true);
            }
        }
    }, [setReachBottom]);

    return (
        <Box
            id={id}
            onScroll={onScroll}
            ref={viewerRef}
            sx={theme => ({
                position: 'relative',
                overflow: 'auto',
                height: '68vh',
                ...(typeof sx === 'function' ? sx(theme) : sx),
            })}
            {...rest}
        >
            <Box
                id={`pdfViewer-${id}`}
                sx={theme => ({
                    '& .page': {
                        direction: 'ltr',
                        margin: '10px 13px',
                        border: `9px solid ${theme.palette.grey['A700']}`,
                        backgroundColor: theme.palette.grey['A700'],
                        [theme.breakpoints.down('md')]: {
                            border: `2px solid ${theme.palette.grey['A700']}`,
                        },
                    },
                })}
            />
            {loading && <Loader staticOnly />}
        </Box>
    );
};

PDFViewer.propTypes = {
    url: PropTypes.string,
    id: PropTypes.string,
    sx: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    setReachBottom: PropTypes.func,
};

export default PDFViewer;
